import { useEffect, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { isStaffUser } from 'sharedHelpers/typeNarrowing';
import {
  useGetUserQuery,
  useGetDashboardClientsPageQuery,
} from 'generated/graphql';
import type { GetDashboardClientsPageQuery } from 'generated/graphql';

import { clientsList } from '../../pages/client/dataTransforms';
import { allClientsListVar, organizationId } from '../graphql/apolloCache';

const FIRST_PAGE_SIZE = 50;
const BACKGROUND_PAGE_SIZE = 250;

const completedPaginationKeys = new Set<string>();

type ClientListItem = ReturnType<typeof allClientsListVar>[number];
type ClientsConnectionForTransform = Parameters<typeof clientsList>[0];
type ClientsConnection = NonNullable<
  GetDashboardClientsPageQuery['getClients']
>;

interface UseClientListHydrationResult {
  clients: ClientListItem[];
  loading: boolean;
  isBackgroundLoadingClients: boolean;
  firstPageResolved: boolean;
}

const mergeDedupeClients = (
  existingClients: ClientListItem[],
  incomingClients: ClientListItem[]
): ClientListItem[] => {
  const byId = new Map<string, ClientListItem>();

  existingClients.forEach((client) => {
    byId.set(client.id, client);
  });

  incomingClients.forEach((client) => {
    byId.set(client.id, client);
  });

  return Array.from(byId.values());
};

const connectionToClientList = (
  connection: GetDashboardClientsPageQuery['getClients']
): ClientListItem[] => {
  const connectionForTransform = (connection as unknown) as ClientsConnectionForTransform;

  return clientsList(connectionForTransform);
};

const mergeConnectionIntoClientListVar = (
  connection: GetDashboardClientsPageQuery['getClients']
): void => {
  if (!connection) return;

  const nextClients = connectionToClientList(connection);

  if (!nextClients.length) return;

  allClientsListVar(mergeDedupeClients(allClientsListVar(), nextClients));
};

export default function useClientListHydration(): UseClientListHydrationResult {
  const clients = useReactiveVar(allClientsListVar);

  const { filterIncludeEventStaff } = useFlags();
  const includeEventStaff = filterIncludeEventStaff || false;
  const skipUserQuery = filterIncludeEventStaff === undefined;

  const [isBackgroundLoadingClients, setIsBackgroundLoadingClients] = useState(
    false
  );

  const { data: serverData, loading: userLoading } = useGetUserQuery({
    variables: {
      filterIncludeEventStaff: includeEventStaff,
      includeClients: false,
    },
    skip: skipUserQuery,
  });

  const staffUser =
    serverData?.getUser && isStaffUser(serverData.getUser)
      ? serverData.getUser
      : undefined;

  const orgId = staffUser?.organization?.id || '';
  const staffUserId = staffUser?.id || '';
  const paginationKey = `${orgId}|${staffUserId}|${includeEventStaff}`;

  const shouldSkipClientsPageQuery =
    !orgId || !staffUserId || completedPaginationKeys.has(paginationKey);

  useEffect(() => {
    if (orgId) {
      organizationId(orgId);
    }
  }, [orgId]);

  const {
    data: dashboardClientsData,
    loading: dashboardClientsLoading,
    fetchMore,
  } = useGetDashboardClientsPageQuery({
    skip: shouldSkipClientsPageQuery,
    variables: {
      filter: {
        organizationId: orgId,
        messageableByStaffId: staffUserId,
        active: true,
      },
      first: FIRST_PAGE_SIZE,
      after: null,
      filterIncludeEventStaff: includeEventStaff,
    },
  });

  const activeRunIdRef = useRef(0);
  const completedKeyRef = useRef<string>('');
  const firstPageHydratedKeyRef = useRef<string>('');
  const backgroundPaginationInProgressRef = useRef(false);

  useEffect(() => {
    activeRunIdRef.current += 1;
    completedKeyRef.current = '';
    firstPageHydratedKeyRef.current = '';
    backgroundPaginationInProgressRef.current = false;
    setIsBackgroundLoadingClients(false);
  }, [paginationKey]);

  useEffect(() => {
    if (!dashboardClientsData?.getClients) return;

    if (firstPageHydratedKeyRef.current === paginationKey) return;

    // Only hydrate the first active page into an empty list.
    // If another page already loaded a partial list and this hook remounted,
    // preserve that partial list and let background pagination continue deduping.
    if (allClientsListVar().length === 0) {
      allClientsListVar(
        connectionToClientList(dashboardClientsData.getClients)
      );
    }

    firstPageHydratedKeyRef.current = paginationKey;
  }, [dashboardClientsData, paginationKey]);

  useEffect(() => {
    if (shouldSkipClientsPageQuery) return undefined;
    if (!dashboardClientsData?.getClients) return undefined;
    if (completedKeyRef.current === paginationKey) return undefined;
    if (backgroundPaginationInProgressRef.current) return undefined;

    const firstActivePage = dashboardClientsData.getClients;
    const runId = activeRunIdRef.current;
    let cancelled = false;

    backgroundPaginationInProgressRef.current = true;
    setIsBackgroundLoadingClients(true);

    const runBackgroundPagination = async () => {
      const fetchAndMergeClientPage = async ({
        active,
        after,
      }: {
        active: boolean;
        after: string | null;
      }): Promise<ClientsConnection | null> => {
        const nextPageResult = await fetchMore({
          variables: {
            filter: {
              organizationId: orgId,
              messageableByStaffId: staffUserId,
              active,
            },
            first: BACKGROUND_PAGE_SIZE,
            after,
            filterIncludeEventStaff: includeEventStaff,
          },
        });

        if (cancelled || runId !== activeRunIdRef.current) {
          return null;
        }

        const nextPageData = nextPageResult.data as GetDashboardClientsPageQuery;
        const nextConnection = nextPageData?.getClients;

        if (!nextConnection) {
          return null;
        }

        mergeConnectionIntoClientListVar(nextConnection);

        return nextConnection;
      };

      try {
        let activeHasNextPage = !!firstActivePage.pageInfo?.hasNextPage;
        let activeAfter = firstActivePage.pageInfo?.endCursor || null;

        /* eslint-disable no-await-in-loop */
        while (activeHasNextPage && activeAfter && !cancelled) {
          const nextConnection: ClientsConnection | null = await fetchAndMergeClientPage(
            {
              active: true,
              after: activeAfter,
            }
          );

          if (!nextConnection) {
            return;
          }

          activeHasNextPage = !!nextConnection.pageInfo?.hasNextPage;
          activeAfter = nextConnection.pageInfo?.endCursor || null;

          if (activeHasNextPage && !activeAfter) {
            activeHasNextPage = false;
          }
        }

        // Hydrate inactive clients too. The UI status filter works client-side,
        // so inactive clients must be present in allClientsListVar.
        let inactiveHasNextPage = true;
        let inactiveAfter: string | null = null;

        while (inactiveHasNextPage && !cancelled) {
          const nextConnection: ClientsConnection | null = await fetchAndMergeClientPage(
            {
              active: false,
              after: inactiveAfter,
            }
          );

          if (!nextConnection) {
            return;
          }

          inactiveHasNextPage = !!nextConnection.pageInfo?.hasNextPage;
          inactiveAfter = nextConnection.pageInfo?.endCursor || null;

          if (inactiveHasNextPage && !inactiveAfter) {
            inactiveHasNextPage = false;
          }
        }
        /* eslint-enable no-await-in-loop */

        if (!cancelled && runId === activeRunIdRef.current) {
          completedKeyRef.current = paginationKey;
          completedPaginationKeys.add(paginationKey);
        }
      } finally {
        if (!cancelled && runId === activeRunIdRef.current) {
          backgroundPaginationInProgressRef.current = false;
          setIsBackgroundLoadingClients(false);
        }
      }
    };

    runBackgroundPagination();

    return () => {
      cancelled = true;
    };
  }, [
    shouldSkipClientsPageQuery,
    dashboardClientsData,
    fetchMore,
    orgId,
    staffUserId,
    includeEventStaff,
    paginationKey,
  ]);

  const loading =
    userLoading ||
    (!shouldSkipClientsPageQuery &&
      dashboardClientsLoading &&
      !dashboardClientsData &&
      clients.length === 0);

  const firstPageResolved =
    clients.length > 0 || Boolean(dashboardClientsData?.getClients);

  return {
    clients,
    loading,
    isBackgroundLoadingClients,
    firstPageResolved,
  };
}
